import { Box, Typography } from '@mui/material'
import { useGridApiRef } from '@mui/x-data-grid-premium'
import { GridColDef, GridDensity } from '@mui/x-data-grid-pro'
import { useUserItems } from 'api/aws/users/useUserItems'
import { formatPhoneNumber } from 'components/Contacts/formatPhoneNumber'
import GlobalDataGrid from 'components/DataGrid/GlobalDataGrid'
import GlobalGridToolbar from 'components/DataGrid/GlobalGridToolbar'
import { getDensityStyles } from 'components/DataGrid/operators/getDensityStyles'
import { renderColorByString } from 'components/DataGrid/renderColorByString'
import React from 'react'
import { EntityAvatar, FlexRow } from 'theme/components'
import { useLayoutContext } from '../../context/LayoutProvider/LayoutProvider'
import { gridContainerStyles } from '../DataGrid/style'
import { defaultColumnOrder, defaultFilterModel, defaultSortModel, defaultVisibilityModel } from './defaultModels'

const TeamsPage = () => {
  const { pinned } = useLayoutContext()
  const { data: users, isLoading: isUsersLoading, isError: isUsersError } = useUserItems()
  const apiRef = useGridApiRef()
  const [density, setDensity] = React.useState<GridDensity>(() => {
    const saved = localStorage.getItem('usersDensityModel') as GridDensity | null
    return saved ?? 'standard'
  })
  const rows = React.useMemo(() => {
    if (!Array.isArray(users) || !users?.length) return []
    return users
      // Only real user profile records — the users query also returns
      // localStorage-clear markers (PK='USER#undefined', isStorageClear=true)
      // that have no user_uuid and crash MUI X with "all rows must have a
      // unique id".
      ?.filter(u => !!u?.user_uuid && !(u as unknown as Record<string, unknown>)?.isStorageClear)
      ?.map(u => ({ ...u, rowId: u.user_uuid }))
      ?.filter(r => !['marketing', 'Slackbot', 'Grobotic']?.includes(r?.name))
  }, [users])
  const densityStyles = getDensityStyles(density)
  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      width: 150,
      headerAlign: 'center',
      sortable: true,
    },
    {
      field: 'user_uuid',
      headerName: 'UUID',
      width: 100,
      headerAlign: 'center',
      sortable: true,
    },
    {
      field: 'firstName',
      headerName: 'First Name',
      width: 100,
      headerAlign: 'center',
      sortable: true,
    },
    {
      field: 'lastName',
      headerName: 'Last Name',
      width: 100,
      headerAlign: 'center',
      sortable: true,
    },
    {
      field: 'role',
      headerName: 'Role',
      width: 100,
      headerAlign: 'center',
      sortable: true,
      renderCell: params => {
        return (
          <FlexRow extraStyles={{ height: '100%' }}>
            <Typography sx={{ color: renderColorByString(params?.value), fontSize: densityStyles?.fontSize }}>
              {params?.value}
            </Typography>
          </FlexRow>
        )
      },
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 175,
      headerAlign: 'center',
      sortable: true,
    },
    {
      field: 'deleted',
      headerName: 'Deleted',
      width: 100,
      headerAlign: 'center',
      sortable: true,
    },
    {
      field: 'title',
      headerName: 'Title',
      width: 200,
      headerAlign: 'center',
      sortable: true,
    },
    {
      field: 'phone',
      headerName: 'Phone',
      width: 100,
      headerAlign: 'center',
      sortable: true,
      renderCell: params => {
        return <Box>{params?.value ? formatPhoneNumber(params?.value) : ''}</Box>
      },
    },
    {
      field: 'linkedIn',
      headerName: 'LinkedIn',
      width: 100,
      headerAlign: 'center',
      sortable: true,
    },
    {
      field: 'image_512',
      headerName: 'Image',
      width: 100,
      headerAlign: 'center',
      sortable: true,
      valueGetter: (_, row) => {
        return row?.name ?? 'No Name'
      },
      renderCell: params => {
        const { row } = params ?? null
        return (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              flexWrap: 'wrap',
            }}
          >
            <EntityAvatar
              name={params?.value}
              photoUrl={row?.image_512 ?? ''}
              size={densityStyles?.size}
              userId={row?.user_uuid}
              tooltip
              enableNav
            />
          </Box>
        )
      },
    },
  ]
  const changeDensity = React.useCallback((newDensity: GridDensity) => {
    setDensity(newDensity)
  }, [])
  const defaultColumnWidths = Object.fromEntries(
    columns
      .filter(col => typeof col.width === 'number') // only include columns that define a width
      .map(col => [col.field, col.width])
  )
  if (isUsersLoading) {
    return <div>loading....</div>
  }

  return (
    <GlobalDataGrid
      columns={columns}
      rows={rows}
      apiRef={apiRef}
      context={'teams'}
      defaultFilterModel={defaultFilterModel}
      defaultVisibilityModel={defaultVisibilityModel}
      defaultSortModel={defaultSortModel}
      defaultGridDensity={'standard'}
      defaultColumnWidths={defaultColumnWidths}
      defaultColumnOrder={defaultColumnOrder}
      onChangeDensity={changeDensity}
      toolbarComponent={GlobalGridToolbar}
      checkboxSelection={false}
      treeData={false}
      gridContainerSx={{
        position: 'relative',
        ...gridContainerStyles,
        pt: pinned ? '60px' : 0.8,
        transition: 'all 0.3s ease',
        height: '98%',
      }}
    />
  )
}

export default TeamsPage
